import React, { useState } from "react";
import styled from "styled-components";
import { signUp } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import robotWhite from "../images/codiii-icon_white.svg";
import robotColor from "../images/codiii-icon.svg";
import mascot from "../images/codiii-mascot.svg";

const TERMS_URL = "https://codiii.com/terms-of-service";      
const PRIVACY_URL = "https://codiii.com/privacy-policy";  

const PageWrap = styled.div`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%);
`;


const RobotPattern = styled.div`
  position: absolute; inset: 0; pointer-events: none; z-index: 1;
  .robot-icon { position: absolute; width: 80px; height: 80px; opacity: 0.1; animation: float 20s infinite linear; }
  .robot-icon:nth-child(odd){ animation-direction: reverse; opacity: 0.08; }
  .robot-icon:nth-child(1){ top:10%; left:5%; animation-delay:0s; }
  .robot-icon:nth-child(2){ top:20%; right:8%; animation-delay:-3s; }
  .robot-icon:nth-child(3){ top:40%; left:12%; animation-delay:-6s; }
  .robot-icon:nth-child(4){ top:60%; right:15%; animation-delay:-9s; }
  .robot-icon:nth-child(5){ top:75%; left:20%; animation-delay:-12s; }
  .robot-icon:nth-child(6){ top:30%; left:50%; animation-delay:-15%; }
  .robot-icon:nth-child(7){ top:80%; right:30%; animation-delay:-18s; }
  .robot-icon:nth-child(8){ top:15%; left:75%; animation-delay:-2s; }
  @keyframes float {
    0%{transform:translateY(0) rotate(0)}
    25%{transform:translateY(-20px) rotate(90deg)}
    50%{transform:translateY(-40px) rotate(180deg)}
    75%{transform:translateY(-20px) rotate(270deg)}
    100%{transform:translateY(0) rotate(360deg)}
  }
  img { width:100%; height:100%; object-fit:contain; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.1)); }
`;

const Card = styled.div`
  position: relative; z-index: 5; width: 100%; max-width: 440px;
  background: rgba(255,255,255,0.95); backdrop-filter: blur(20px);
  border-radius: 24px; padding: 48px;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1);
  animation: slideUp 0.8s ease-out;
  @keyframes slideUp { from { opacity:0; transform: translateY(30px) scale(0.95); } to { opacity:1; transform: translateY(0) scale(1); } }
  @media (max-width: 480px){ margin: 20px; padding: 32px 24px; max-width: none; }
`;

const LogoSection = styled.div`
  text-align: center; margin-bottom: 40px;
  .logo-container { display:inline-flex; align-items:center; justify-content:center; margin-bottom:16px; }
  .logo { width:64px; height:64px; margin-right:12px; }
  .brand-text { font-size:32px; font-weight:800; color:#1f2937; letter-spacing:-0.02em; }
  .brand-dots { color:#f97316; }
  .tagline { color:#6b7280; font-size:14px; font-weight:500; text-transform:uppercase; letter-spacing:0.5px; margin-top:8px; }
`;

const H1 = styled.h1` font-size:28px; font-weight:700; color:#1f2937; margin-bottom:8px; text-align:center; `;
const Subtitle = styled.p` color:#6b7280; font-size:16px; text-align:center; margin-bottom:32px; line-height:1.5; `;
const FormGroup = styled.div` margin-bottom:24px; `;
const Label = styled.label` display:block; color:#374151; font-weight:600; font-size:14px; margin-bottom:8px; `;
const Input = styled.input`
  width:100%; padding:16px 20px; border:2px solid #e5e7eb; border-radius:16px; font-size:16px; background:rgba(255,255,255,0.9);
  transition: all .3s ease;
  &:focus{ outline:none; border-color:#f97316; box-shadow:0 0 0 4px rgba(249,115,22,0.1), 0 4px 20px rgba(0,0,0,0.1); background:#fff; }
  &::placeholder{ color:#9ca3af; }
`;
const Row = styled.div` display:flex; align-items:center; gap:12px; margin:24px 0; `;
const Small = styled.span` color:#6b7280; font-size:14px; `;
const PrimaryBtn = styled.button`
  width:100%; background:linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  border:none; border-radius:16px; color:#fff; padding:18px 24px; font-size:16px; font-weight:700; cursor:pointer; transition:all .3s ease;
  box-shadow: 0 8px 20px rgba(249,115,22,0.3), 0 2px 8px rgba(0,0,0,0.1);
  position:relative; overflow:hidden; margin-top: 4px;
  &:before{ content:''; position:absolute; top:0; left:-100%; width:100%; height:100%;
    background:linear-gradient(90deg, transparent, rgba(255,255,255,.2), transparent); transition:left .5s ease; }
  &:hover:before{ left:100%; }
  &:hover{ transform: translateY(-2px); box-shadow: 0 12px 30px rgba(249,115,22,.4), 0 4px 15px rgba(0,0,0,.15); }
  &:active{ transform: translateY(0); }
`;
const MutedBtn = styled(PrimaryBtn)` background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); `;
const Footer = styled.div` text-align:center; margin-top:32px; color:#6b7280; font-size:14px; `;
const CheckboxRow = styled.label`
  --cb: 20px;

  display: flex;
  align-items: flex-start;      
  gap: 12px;
  cursor: pointer;
  user-select: none;
  line-height: 1.5;            
  min-height: var(--cb);        
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const CheckboxBox = styled.span`
  width: var(--cb);
  height: var(--cb);
  border-radius: 6px;
  border: 2px solid #e5e7eb;
  background: #fff;
  display: inline-flex;         
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  flex-shrink: 0;               
  transform: translateZ(0);     
`;

const CheckMark = styled.span`
  width: 10px;
  height: 10px;
  transform: rotate(45deg);
  border-right: 3px solid transparent;
  border-bottom: 3px solid transparent;
  transition: border-color .15s ease;
`;

const CheckboxText = styled.span`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;             
  a { color: #7c3aed; }         
`;


function CheckboxField({ id, checked, onChange, children }) {
  const isOn = !!checked;
  return (
    <CheckboxRow htmlFor={id}>
      <HiddenCheckbox id={id} checked={checked} onChange={onChange} />
      <CheckboxBox
        style={{
          background: isOn ? '#f97316' : '#fff',
          borderColor: isOn ? '#f97316' : '#e5e7eb'
        }}
      >
        <CheckMark
          style={{
            borderRightColor: isOn ? '#fff' : 'transparent',
            borderBottomColor: isOn ? '#fff' : 'transparent'
          }}
        />
      </CheckboxBox>
      <CheckboxText>{children}</CheckboxText>
    </CheckboxRow>
  );
}


function usePasswordToggle() {
  const [show, setShow] = useState(false);
  const type = show ? 'text' : 'password';
  return { type, show, setShow };
}

async function parseJsonSafe(resp) {
  const ct = resp.headers.get('content-type') || '';
  const text = await resp.text(); 
  if (ct.includes('application/json') && text) {
    try { return JSON.parse(text); } catch (_) {  }
  }
  return text || null; 
}


const TERMS_VERSION = "2025-09-02";

export default function HostedAuth(){
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const pw = usePasswordToggle();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  async function onSignUp(e){
    e.preventDefault();
    setError("");

    if(!agree){ setError("Please agree to the Terms & Conditions."); return; }
    if(!email || !password || !confirm || !name){ setError("Please fill all fields."); return; }
    if(password !== confirm){ setError("Passwords do not match."); return; }

    setSubmitting(true);
    try{
        const nowIso = new Date().toISOString();
        const res = await fetch(`${API_BASE}/api/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password, name,
            termsAccepted: true,
            termsVersion: TERMS_VERSION,
            termsDate: new Date().toISOString()
          })
        });

        const data = await parseJsonSafe(res);
        if (!res.ok) {
          const msg = typeof data === 'string' ? data : (data?.detail || 'Sign up failed');
          throw new Error(msg);
        }

        nav('/auth/confirm', { state: { email } });

    }catch(err){
        setError(err.message);
    }finally{
        setSubmitting(false);
    }
    }


  return (
    <PageWrap>
      <RobotPattern>
        {[1,2,3,4,5,6,7,8].map((i) => (
          <div key={i} className="robot-icon">
            <img
              src={i <= 5 ? robotColor : robotWhite}
              alt="CODiii Robot"
            />
          </div>
        ))}
      </RobotPattern>

      <Card>
        <LogoSection>
          <div className="logo-container">
            <div className="logo"><img src={mascot} alt="CODiii Logo" /></div>
            <span className="brand-text">COD<span className="brand-dots">iii</span></span>
          </div>
          <div className="tagline">The Design & Construction Brain</div>
        </LogoSection>

        <H1>Create your account</H1>
        <Subtitle>Sign up to get started.</Subtitle>

        <form onSubmit={onSignUp}>
          <FormGroup>
            <Label htmlFor="signup-email">Email address</Label>
            <Input id="signup-email" type="email" placeholder="name@host.com"
              value={email} onChange={e=>setEmail(e.target.value)} required />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="full-name">Name</Label>
            <Input id="full-name" type="text" placeholder="Enter your full name"
              value={name} onChange={e=>setName(e.target.value)} required />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-password">Password</Label>
            <Input id="signup-password" type={pw.type} placeholder="Enter password"
              value={password} onChange={e=>setPassword(e.target.value)} required />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirm-password">Confirm password</Label>
            <Input id="confirm-password" type={pw.type} placeholder="Reenter password"
              value={confirm} onChange={e=>setConfirm(e.target.value)} required />
          </FormGroup>
          {/* Show password */}
         <Row>
          <CheckboxField
            id="show-signup-password"
            checked={pw.show}
            onChange={e=>pw.setShow(e.target.checked)}
          >
            Show password
          </CheckboxField>
        </Row>
        {/* Terms */}
          <Row>
          <CheckboxField
            id="agree"
            checked={agree}
            onChange={e=>setAgree(e.target.checked)}
          >
            I agree to the{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
            {PRIVACY_URL ? (
              <> and acknowledge the <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</>
            ) : "."}
          </CheckboxField>
        </Row>

          {error && (
            <div style={{
              color:'#b91c1c', background:'rgba(185,28,28,0.08)',
              border:'1px solid rgba(185,28,28,0.25)', padding:'12px 14px',
              borderRadius:12, marginBottom:16
            }}>
              {error}
            </div>
          )}

          <PrimaryBtn type="submit" disabled={submitting}>
            {submitting ? 'Creating account…' : 'Sign up'}
          </PrimaryBtn>
          <MutedBtn type="button" onClick={()=>nav('/auth/signin')} style={{marginTop:8}}>
            Back to Sign In
          </MutedBtn>
        </form>

        <Footer>
          Already have an account? <a onClick={(e)=>{e.preventDefault(); nav('/auth/signin');}} href="/auth/signin">Sign in</a>
        </Footer>
      </Card>
    </PageWrap>
  );
}
